import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import DashboardLayout from "../DashboardLayout";
import NavigationMenu from "../NavigationMenu";

const ExcelUploader = () => {
  // State management
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [errors, setErrors] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [authError, setAuthError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [failedRecords, setFailedRecords] = useState([]);
  const [batchInfo, setBatchInfo] = useState({
    totalBatches: 0,
    processedBatches: 0,
    currentBatch: 0
  });

  // Configuration
  const BATCH_SIZE = 5;
  const MAX_RETRIES = 30;
  const INITIAL_DELAY = 8000;
  const MAX_DELAY = 30000;

  // Refs for cancellation
  const cancelRequested = useRef(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: null });

        // Map Excel columns to customer_lists table fields
        const mappedData = jsonData.map(row => {
          // Get the current user ID from localStorage/sessionStorage
          const currentUserId = localStorage.getItem("user_id") || sessionStorage.getItem("user_id");

          return {
            group_name: row['group_name'] ?? 'Doctor', // Default to 'Doctor' if not provided
            batch: row['batch'] ?? null,
            division: row['division'] ?? null,
            name: row['name'] ?? null,
            email: row['email'] ?? null,
            phone: row['phone'] ? row['phone'].toString() : null,
            imported_by: currentUserId ? parseInt(currentUserId) : null, // Use current user ID
          };
        }).filter(row => row.name && row.email); // Filter out rows without required fields

        setPreviewData(mappedData);
        setBatchInfo(prev => ({
          ...prev,
          totalBatches: Math.ceil(mappedData.length / BATCH_SIZE)
        }));
      } catch (error) {
        console.error("Error processing file:", error);
        setAuthError("Failed to process the Excel file. Please check the format.");
      }
    };
    reader.onerror = () => {
      setAuthError("Error reading file. Please try again.");
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const processRecord = async (record, attempt = 1) => {
    const { url, headers } = getApiConfig();
    
    // Log the payload for debugging
    console.log("Processing record:", JSON.stringify(record, null, 2));
    
    try {
      const response = await axios.post(url, record, { headers });
      return { success: true, data: response.data };
    } catch (error) {
      if (error.response?.status === 429 && attempt <= MAX_RETRIES) {
        const delay = Math.min(INITIAL_DELAY * Math.pow(2, attempt - 1), MAX_DELAY);
        setIsRateLimited(true);
        await new Promise(resolve => setTimeout(resolve, delay));
        setIsRateLimited(false);
        return processRecord(record, attempt + 1);
      }
      return {
        success: false,
        error: error.response?.data || error.message
      };
    }
  };

  const processBatch = async (batch, batchNumber) => {
    if (cancelRequested.current) return { successes: 0, failures: [] };

    setBatchInfo(prev => ({ ...prev, currentBatch: batchNumber }));
    
    const batchResults = await Promise.all(
      batch.map(record => processRecord(record))
    );
    
    const successes = batchResults.filter(r => r.success).length;
    const failures = batchResults
      .filter(r => !r.success)
      .map((result, index) => ({
        row: ((batchNumber - 1) * BATCH_SIZE) + index + 1,
        data: batch[index],
        error: result.error
      }));
    
    return { successes, failures };
  };

  const processAllBatches = async (records) => {
    cancelRequested.current = false;
    let totalSuccesses = 0;
    let totalFailures = [];

    for (let i = 0; i < records.length; i += BATCH_SIZE) {
      if (cancelRequested.current) break;
      
      const batch = records.slice(i, i + BATCH_SIZE);
      const batchNumber = (i / BATCH_SIZE) + 1;
      const { successes, failures } = await processBatch(batch, batchNumber);
      
      totalSuccesses += successes;
      totalFailures = [...totalFailures, ...failures];
      
      setSuccessCount(totalSuccesses);
      setErrorCount(totalFailures.length);
      setErrors(totalFailures);
      setFailedRecords(totalFailures);
      setProgress(Math.round(((i + BATCH_SIZE) / records.length) * 100));
      setBatchInfo(prev => ({
        ...prev,
        processedBatches: batchNumber
      }));
    }
    
    return { totalSuccesses, totalFailures };
  };

  const getApiConfig = () => {
    const baseURL = 'https://api.miplapp.in/public/api/';
    
    // Use the customers route as per your Laravel routes
    return {
      url: `${baseURL}email/customers`, // This will hit the POST /api/customers route
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };
  };

  const uploadData = async () => {
    if (!file || previewData.length === 0) return;

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      setAuthError("No authentication token found. Please log in.");
      return;
    }

    setUploading(true);
    setSuccessCount(0);
    setErrorCount(0);
    setErrors([]);
    setAuthError(null);
    setProgress(0);
    setIsRateLimited(false);
    setFailedRecords([]);
    setBatchInfo({
      totalBatches: Math.ceil(previewData.length / BATCH_SIZE),
      processedBatches: 0,
      currentBatch: 0
    });

    try {
      // Filter out records without required fields (name and email are required)
      const recordsToProcess = previewData.filter(row => 
        row.name && row.email
      );

      if (recordsToProcess.length === 0) {
        setAuthError("No valid records found. Each record must have name and email.");
        setUploading(false);
        return;
      }

      await processAllBatches(recordsToProcess);
    } catch (error) {
      if (!cancelRequested.current) {
        console.error("Upload error:", error);
        setAuthError("An unexpected error occurred. Please try again.");
      }
    } finally {
      if (!cancelRequested.current) {
        setUploading(false);
      }
    }
  };

  const cancelUpload = () => {
    cancelRequested.current = true;
    setUploading(false);
  };

  const exportFailedRecords = () => {
    if (failedRecords.length === 0) return;

    try {
      const exportData = failedRecords.map(record => ({
        'Row Number': record.row,
        'group_name': record.data.group_name || '',
        'batch': record.data.batch || '',
        'division': record.data.division || '',
        'name': record.data.name || '',
        'email': record.data.email || '',
        'phone': record.data.phone || '',
        'imported_by': record.data.imported_by || '',
        'Error Message': typeof record.error === 'object'
          ? JSON.stringify(record.error, null, 2)
          : record.error
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Failed Records");
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `Failed_Customer_Lists_${timestamp}.xlsx`;
      
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Export failed:", error);
      setAuthError("Failed to export error records. Please try again.");
    }
  };

  const getTemplateDownloadLink = (returnObject = false) => {
    const templateObject = {
      'group_name': 'Doctor',
      'batch': 'Batch5',
      'division': 'Gastro',
      'name': 'Dr. John Doe',
      'email': 'john.doe@example.com',
      'phone': '9876543210',
      'imported_by': '' // This will be auto-filled with current user ID during upload
    };

    if (returnObject) {
      return templateObject;
    }

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet([templateObject]);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customer Lists');
    XLSX.writeFile(workbook, 'Customer_Lists_Template.xlsx');
  };

  function getPreviewHeaders() {
    return [
      'Group Name', 'Batch', 'Division', 'Name', 
      'Email', 'Phone', 'Imported By'
    ];
  }

  return (
    <DashboardLayout title="Bulk Upload" menu={<NavigationMenu />}>
      <div className="p-5 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Bulk Upload Customer Lists</h2>

        <div className="mb-6 p-4 bg-blue-50 rounded-md">
          <p className="text-blue-800 mb-2">
            Need a template?{' '}
            <button 
              onClick={() => getTemplateDownloadLink()} 
              className="text-blue-600 hover:underline font-medium"
            >
              Download Customer Lists Excel Template
            </button>
          </p>
          <p className="text-sm text-blue-700">
            The template matches your Excel file structure. Required fields: name and email.
            <br />
            <span className="font-semibold">Note:</span> The 'imported_by' field will be automatically set to your user ID.
          </p>
        </div>

        {authError && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
            <p>{authError}</p>
          </div>
        )}

        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">Upload Excel File</label>
          <input 
            type="file" 
            accept=".xlsx, .xls, .csv" 
            onChange={handleFileChange} 
            disabled={uploading} 
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed" 
          />
        </div>

        {previewData.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Preview (First 5 Rows)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {getPreviewHeaders().map(header => (
                      <th 
                        key={header} 
                        className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewData.slice(0, 5).map((row, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                        {row.group_name || ''}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                        {row.batch || ''}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                        {row.division || ''}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                        {row.name || ''}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                        {row.email || ''}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                        {row.phone || ''}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                        {row.imported_by || 'Current User'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-sm text-gray-600">Total rows found: {previewData.length}</p>
          </div>
        )}

        {previewData.length > 0 && (
          <div className="mb-6">
            <div className="flex space-x-4 mb-4">
              <button 
                onClick={uploadData} 
                disabled={uploading} 
                className={`px-4 py-2 rounded-md text-white font-medium ${uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'} focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2`}
              >
                {uploading ? 'Uploading...' : 'Upload Customer Lists Data'}
              </button>
              {uploading && (
                <button 
                  onClick={cancelUpload} 
                  className="px-4 py-2 rounded-md bg-red-500 text-white font-medium hover:bg-red-600"
                >
                  Cancel
                </button>
              )}
            </div>
            {(uploading || isRateLimited) && (
              <div className="mt-4">
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">
                    Batch {batchInfo.currentBatch} of {batchInfo.totalBatches} 
                    {isRateLimited && <span className="text-yellow-600 ml-2">(Rate limit active, retrying...)</span>}
                  </span>
                  <span className="text-sm font-medium text-gray-700">{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        )}

        {(successCount > 0 || errorCount > 0) && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Upload Results</h3>
            <div className="flex space-x-4 items-center">
              <p className="text-green-600 font-medium">Successful: {successCount}</p>
              <p className="text-red-600 font-medium">Failed: {errorCount}</p>
              {errorCount > 0 && (
                <button 
                  onClick={exportFailedRecords} 
                  className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Export Failed Records
                </button>
              )}
            </div>
          </div>
        )}

        {errors.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Error Details</h3>
            <div className="space-y-3 max-h-60 overflow-y-auto pr-2 border rounded-md p-3">
              {errors.slice(0, 10).map((err, index) => (
                <div key={index} className="p-3 border border-red-200 bg-red-50 rounded-md">
                  <p className="font-medium text-sm text-red-800">Row {err.row}: {err.data.name || 'Unknown'}</p>
                  <p className="text-xs text-gray-600 mt-1">Email: {err.data.email || 'N/A'}</p>
                  <p className="text-red-600 text-sm mt-2">
                    Error: {typeof err.error === 'object' 
                      ? err.error.message || JSON.stringify(err.error) 
                      : err.error}
                  </p>
                </div>
              ))}
              {errors.length > 10 && (
                <p className="text-sm text-gray-500">
                  Showing first 10 of {errors.length} errors. Export to see all.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ExcelUploader;